import React, { memo, useRef, useMemo, useEffect, useState } from "react";
import ReactDOM from "react-dom";
import WinToolbar from "./ToolBar";
import { Modal, message } from "antd";

import "./index.less";
import { FocusSDK, isFocusEnv } from "@/utils";

/**
 * 复制样式，主要把调用窗口的css全部复制到小窗口上
 * @param sourceDoc
 * @param targetDoc
 */
const copyStyles = (sourceDoc: any, targetDoc: any) => {
  // icon font
  const iconfontSrc = `${process.env.PUBLIC_URL || window.location.origin}/iconfont.js`;
  const script = sourceDoc.createElement("script");
  script.src = iconfontSrc;
  targetDoc.body.appendChild(script);

  Array.from(sourceDoc.styleSheets).forEach((styleSheet: any) => {
    if (styleSheet.cssRules) {
      // 内联样式
      const newStyleEl = sourceDoc.createElement("style");

      Array.from(styleSheet.cssRules).forEach((cssRule: any) => {
        newStyleEl.appendChild(sourceDoc.createTextNode(cssRule.cssText));
      });

      targetDoc.head.appendChild(newStyleEl);
    }
    if (styleSheet.href) {
      // 外联样式
      const newLinkEl = sourceDoc.createElement("link");

      newLinkEl.rel = "stylesheet";
      newLinkEl.href = styleSheet.href;
      targetDoc.head.appendChild(newLinkEl);
    }
  });
};

const WindowPortal = (props: {
  children: any;
  onBlur?: Function;
  onClose?: Function;
  windowRef?: React.MutableRefObject<any>;
  visible: boolean;
  closeAfterBlur?: boolean;
  winOptions?: any;
  name?: string;
  showToolBar?: boolean;
  beforeUnloadModal?: {
    title: string;
    icon: any;
    content: string;
    okText: string;
    cancelText: string;
  };
  beforeUnloadMessage?: string;
  beforeUnloadCallback?: Function;
  isPortalNode?: boolean;
  winId?: string;
  parentId?: number;
  onWinClick?: Function;
}) => {
  const {
    children,
    closeAfterBlur,
    onBlur,
    onClose,
    windowRef,
    visible,
    winOptions,
    name,
    showToolBar = false,
    beforeUnloadModal,
    beforeUnloadMessage,
    beforeUnloadCallback,
    isPortalNode = true,
    winId,
    parentId,
    onWinClick
  } = props;
  const [portalNode, setPortalNode] = useState<any>(null);
  const windowInstance = useRef<any>(null);
  const winOptionRef = useRef({});
  const toolBarEl = useMemo(() => {
    if (visible && showToolBar) {
      return document.createElement("div");
    }
    return null;
  }, [showToolBar, visible]);
  const containerEl = useMemo(() => {
    if (visible && isPortalNode) {
      return document.createElement("div");
    }
    return null;
  }, [isPortalNode, visible]);
  winOptionRef.current = winOptions || {};
  useEffect(() => {
    if (!visible) {
      if (windowInstance.current) {
        windowInstance.current.close();
        windowInstance.current = null;
      }
      return;
    }
    // 默认选项
    const defaultBrowserWindowOptions = {
      transparent: false,
      backgroundColor: "#ffffff",
      width: 400,
      minWidth: 120,
      minHeight: 120,
      height: 400
    };

    const browserwinOptions: any = {
      ...defaultBrowserWindowOptions,
      ...winOptionRef.current,
      title: name
    };
    const { x, width, y, height } = browserwinOptions;
    // 如果没有传递x和y，则打开时默认居中
    const left = x === undefined ? window.screen.width / 2 - width / 2 : x;
    let top = y === undefined ? window.screen.height / 2 - height / 2 : y;
    browserwinOptions.x = left;
    browserwinOptions.y = top;
    if (showToolBar) {
      browserwinOptions.height += 30;
    }

    windowInstance.current = window.open(
      "",
      "_blank",
      `__portalType=modal,${
        parentId ? "parentId=" + parentId + "," : ""
      }preferences=${encodeURIComponent(JSON.stringify(browserwinOptions))}`
    );
    windowRef && (windowRef.current = windowInstance.current);
    if (!windowInstance.current) return;
    if (showToolBar) {
      ReactDOM.render(
        <WinToolbar title={name} FocusSDK={windowInstance.current.FocusSDK} />,
        toolBarEl as HTMLDivElement
      );
      windowInstance.current.document.body.appendChild(toolBarEl);
    }
    windowInstance.current.document.head.innerHTML = "";
    if (isPortalNode) {
      containerEl?.setAttribute("class", `ele-modal-wrap ${showToolBar ? "with-tool-bar" : ""}`);
      windowInstance.current.document.body.appendChild(containerEl);
      setPortalNode(containerEl);
    } else {
      setPortalNode(windowInstance.current.document.body);
    }
    copyStyles(document, windowInstance.current.document);
    // windowInstance.current?.FocusSDK.openDevTools();
  }, [name, visible, containerEl, windowRef, showToolBar, toolBarEl, isPortalNode, parentId]);

  useEffect(() => {
    if (!windowInstance.current) return;

    const onBlurInner = () => {
      // 默认失焦关闭
      if (closeAfterBlur && windowInstance.current) {
        windowInstance.current.close();
        windowInstance.current = null;
        onClose?.();
      }
      if (onBlur) {
        onBlur();
      }
    };
    const onBeforeunload = (e: { returnValue: boolean }) => {
      if (beforeUnloadCallback) {
        beforeUnloadCallback();
      }
      onClose?.();
      windowRef && (windowRef.current = null);
      if (!windowInstance.current) return;
      windowInstance.current.close();
      windowInstance.current = null;
    };
    const onWinClickInner = () => {
      onWinClick?.(windowInstance.current?.FocusSDK?.windowInfo?.windowId);
    };
    windowInstance.current.addEventListener("blur", onBlurInner);
    windowInstance.current.addEventListener("beforeunload", onBeforeunload);
    windowInstance.current.addEventListener("click", onWinClickInner);
    return () => {
      if (!windowInstance.current) return;
      windowInstance.current.removeEventListener("blur", onBlurInner);
      windowInstance.current.removeEventListener("beforeunload", onBeforeunload);
      windowInstance.current.removeEventListener("click", onWinClickInner);
    };
  }, [
    visible,
    onBlur,
    onClose,
    closeAfterBlur,
    windowRef,
    beforeUnloadMessage,
    beforeUnloadCallback,
    winId,
    onWinClick
  ]);
  useEffect(() => {
    const closeWin = () => {
      windowRef && (windowRef.current = null);
      if (!windowInstance.current) return;
      windowInstance.current.close();
      windowInstance.current = null;
    };
    window.addEventListener("beforeunload", closeWin);
    return () => {
      window.removeEventListener("beforeunload", closeWin);
      if (!windowInstance.current) return;
      windowInstance.current.close();
      windowInstance.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!visible || !portalNode) {
    return null;
  }

  return ReactDOM.createPortal(children, portalNode);
};
/**
 * 小窗组件，渲染使用createPortal，事件在主窗口处理，可通过 windowRef.current.FocusSDK调用方法
 */
export default memo(WindowPortal);
